import React, { useEffect, useRef, useState } from 'react';
import { Image, Tooltip } from 'antd';
import { LeftOutlined, RightOutlined, PlayCircleFilled } from '@ant-design/icons';
import { useMethods } from '@lhb/hook';
import V2Empty from '@/common/components/Data/V2Empty';
import V2VideoPlayer, { V2VideoHandles } from '@/common/components/Data/V2VideoPlayer';
import { MediaData, ReportMedia } from '../../types';
import styles from './index.module.less';

// 2026-1029迭代/商场慢闪场景体验优化（文档：https://confluence.lanhanba.com/pages/viewpage.action?pageId=172108153）：保留空分类，默认落位图；切换时先暂停旧视频。

type MediaCategory = 'layout' | 'scene' | 'videos';
const DEFAULT_VIDEO_THUMBNAIL = 'https://staticres.linhuiba.com/project-custom/locationpc/chancepoint/bg_mp4_default.png';

const CATEGORY_ITEMS: Array<{ value: MediaCategory; label: string }> = [
  { value: 'layout', label: '落位图' },
  { value: 'scene', label: '周边实景' },
  { value: 'videos', label: '动线视频' },
];

const MediaGallery: React.FC<{ data: MediaData }> = ({ data }) => {
  const [category, setCategory] = useState<MediaCategory>('layout');
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState(false);
  const thumbnails = useRef<HTMLDivElement>(null);
  const player = useRef<V2VideoHandles | null>(null);
  const items = data[category];
  const current = items[index];

  const methods = useMethods({
    pauseVideo() {
      const instance = player.current?.video.current;
      if (instance && !instance.isDisposed?.()) instance.pause?.();
    },
    reset() {
      methods.pauseVideo();
      setCategory('layout');
      setIndex(0);
      setFailed(false);
    },
    changeCategory(event: React.MouseEvent<HTMLButtonElement>) {
      const next = event.currentTarget.dataset.category as MediaCategory;
      if (!data[next] || next === category) return;
      methods.pauseVideo();
      setCategory(next);
      setIndex(0);
      setFailed(false);
    },
    selectItem(next: number) {
      methods.pauseVideo();
      setIndex(next);
      setFailed(false);
    },
    previous() { methods.selectItem((index - 1 + items.length) % items.length); },
    next() { methods.selectItem((index + 1) % items.length); },
    clickThumbnail(event: React.MouseEvent<HTMLButtonElement>) { methods.selectItem(Number(event.currentTarget.dataset.index)); },
    scrollThumbnail() {
      const selected = thumbnails.current?.querySelector<HTMLButtonElement>(`[data-index="${index}"]`);
      if (selected && thumbnails.current) thumbnails.current.scrollTo({ left: selected.offsetLeft - thumbnails.current.clientWidth / 2 + selected.offsetWidth / 2, behavior: 'smooth' });
    },
    imageError() { setFailed(true); return false; },
    thumbnailError(event: React.SyntheticEvent<HTMLImageElement>) {
      const image = event.currentTarget;
      if (image.src !== DEFAULT_VIDEO_THUMBNAIL) image.src = DEFAULT_VIDEO_THUMBNAIL;
    },
    capturePlayer(handle: V2VideoHandles | null) { if (handle) player.current = handle; },
    renderThumbnail(item: ReportMedia, itemIndex: number) {
      return <button key={`${item.url}-${itemIndex}`} type='button' data-index={itemIndex} onClick={methods.clickThumbnail}
        className={itemIndex === index ? styles.selected : ''} aria-label={`查看${item.name || '媒体'} ${itemIndex + 1}`} aria-pressed={itemIndex === index}>
        <img className={category === 'layout' ? styles.layoutThumbnail : ''} src={item.thumbnail || (category === 'videos' ? DEFAULT_VIDEO_THUMBNAIL : item.url)} onError={category === 'videos' ? methods.thumbnailError : undefined} alt={item.name || `媒体 ${itemIndex + 1}`} loading='lazy' />
        {category === 'videos' && <PlayCircleFilled className={styles.thumbnailPlayIcon} />}
      </button>;
    },
  });

  useEffect(methods.reset, [data]);
  useEffect(methods.scrollThumbnail, [index, category]);
  useEffect(() => methods.pauseVideo, []);

  return <section className={styles.gallery}>
    <div className={styles.toolbar} role='tablist' aria-label='点位媒体分类'>
      {CATEGORY_ITEMS.map(item => <button key={item.value} type='button' role='tab' data-category={item.value} onClick={methods.changeCategory}
        className={category === item.value ? styles.activeCategory : ''} aria-selected={category === item.value}>{item.label} <span>{data[item.value].length}</span></button>)}
    </div>
    <div className={styles.stage}>
      {current ? <>
        {category === 'videos' ? <V2VideoPlayer key={current.url + index} ref={methods.capturePlayer} src={current.url} width='100%' height='100%' options={{ autoplay: false, preload: 'metadata', controls: true }} />
          : failed ? <V2Empty type='fail' /> : <Image src={current.url} alt={current.name} onError={methods.imageError} />}
        {items.length > 1 && <>
          <Tooltip title='上一张'><button type='button' className={styles.previous} aria-label='上一张' onClick={methods.previous}><LeftOutlined /></button></Tooltip>
          <Tooltip title='下一张'><button type='button' className={styles.next} aria-label='下一张' onClick={methods.next}><RightOutlined /></button></Tooltip>
        </>}
      </> : <V2Empty customTip={`暂无${CATEGORY_ITEMS.find(item => item.value === category)?.label || '媒体'}素材，可在机会点表单中上传`} />}
      {!!items.length && <div ref={thumbnails} className={styles.thumbnails}>{items.map(methods.renderThumbnail)}</div>}
    </div>
  </section>;
};

export default MediaGallery;
